/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo, useState } from "react";
import usePluginUtils from "../hooks/usePluginUtils";
import { useBooking } from "../hooks/useBooking";
import { Navigate } from "react-router-dom";
import { ROUTES } from "../constants/constants";
import Home from "../pages/Home";
import BookingTypeChooser from "../pages/BookingTypeChooser";
import { useServices } from "../lib/react-query/useServices";
import { useLocations } from "../lib/react-query/useLocations";
import LoadingIndicator from "./LoadingIndicator";
import { useRedirectRoutes } from "../hooks/useRedirectRoutes";
import useWidgetCallbackParams from "../hooks/useWidgetCallbackParams";
import { getPendingPayment, clearPendingPayment } from "../utils/pendingPayment";
import { writePaymentCallbackHash, getPaymentStatusRoute } from "../utils/paymentCallback";
import { useActiveFlow } from "../hooks/useActiveFlow";

const DefaultRouteHandler = () => {
  const { clearParams, hasParams } = useWidgetCallbackParams();
  const { isPluginEnabled } = usePluginUtils();
  const { setSelectedLocation, selectedLocation } = useBooking();
  const redirectRoute = useRedirectRoutes();
  const activeFlow = useActiveFlow();
  const { data: locations, isLoading } = useLocations({ enabled: isPluginEnabled("location") });

  // A still-pending marker here means this document reloaded while a payment popup was open, so
  // the relay's postMessage found no listener - resume to the status page instead of Home. Read
  // once on mount; a callback hash in the URL is the fresher signal and wins.
  const [pendingPayment] = useState(() => (redirectRoute ? null : getPendingPayment()));
  const [isPendingPaymentReady, setIsPendingPaymentReady] = useState(false);

  useEffect(() => {
    if (pendingPayment) {
      // Hash must exist before the status page mounts - hence the flag, not a direct <Navigate>.
      writePaymentCallbackHash(pendingPayment.invoiceId, pendingPayment.system);
      clearPendingPayment();
      setIsPendingPaymentReady(true);
    } else if (redirectRoute) {
      // Marker lost the race to a real callback hash - drop it, or it would resume an abandoned
      // payment on some later boot in this tab.
      clearPendingPayment();
    }
  }, [pendingPayment, redirectRoute]);

  const defaultLocation = useMemo(() => {
    return locations?.length === 1 ? locations[0] : null;
  }, [locations]);

  const shouldRedirectToLocations = isPluginEnabled("location") && !selectedLocation;
  const classesEnabled = isPluginEnabled("classes");
  const { data: services, isLoading: isServicesLoading } = useServices(
    {
      "filter[mark_combine]": true,
      "filter[type]": "service",
      ...(isPluginEnabled("location") && { "filter[location_id]": selectedLocation?.id }),
    },
    { enabled: classesEnabled && !shouldRedirectToLocations }
  );

  useEffect(() => {
    if (!redirectRoute && hasParams) {
      // Delay clearing params to allow time for error toasts to be displayed
      // from useRedirectRoutes hook (e.g., when plugin is disabled)
      const timeoutId = setTimeout(() => {
        clearParams();
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [clearParams, hasParams, redirectRoute]);

  useEffect(() => {
    !isLoading && defaultLocation && setSelectedLocation(defaultLocation);
  }, [defaultLocation, isLoading, setSelectedLocation]);

  if (pendingPayment) {
    return isPendingPaymentReady ? (
      <Navigate replace to={getPaymentStatusRoute(activeFlow)} />
    ) : (
      <LoadingIndicator page />
    );
  }

  if (isLoading) {
    return <LoadingIndicator page />;
  }

  if (redirectRoute) {
    return <Navigate replace to={redirectRoute} />;
  }

  if (shouldRedirectToLocations) {
    return <Navigate replace to={ROUTES.LOCATIONS} />;
  }

  if (classesEnabled) {
    if (isServicesLoading) return <LoadingIndicator page />;
    if (!services?.length) return <Navigate replace to={ROUTES.CLASSES} />;
    return <BookingTypeChooser />;
  }

  return <Home />;
};

export default DefaultRouteHandler;
